import React from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, useRole } from './components/ProtectedRoute/ProtectedRoute';
import { UserRole } from './types/Property';
import "./App.css";

// Componentes existentes
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";

// Novos componentes
import { HomePage } from './components/Home/HomePage';
import { SearchPage } from './components/Search/SearchPage';
import { LocadorPage } from './components/Locador/LocadorPage';
import { AdminPage } from './components/Admin/AdminPage';
import { LoginPage } from './components/Admin/LoginPage';
import { AboutPage } from './components/About/AboutPage';
import { ContactPage } from './components/Contact/ContactPage';
import { FavoritosPage } from './components/Favoritos/FavoritosPage';

// Componente para Locadores
const LocadorDashboard: React.FC = () => {
  const { isLocador } = useRole();
  
  if (!isLocador()) {
    return (
      <div className="unauthorized-message">
        <h2>Acesso Restrito</h2>
        <p>Esta área é exclusiva para locadores.</p>
      </div>
    );
  }

  return <LocadorPage />;
};

// Componente para Administradores
const AdminDashboard: React.FC = () => {
  return <AdminPage />;
};

// Componente para redirecionar imóvel por código para a busca
const PropertyRedirect: React.FC = () => {
  const { codigo } = useParams<{ codigo: string }>();
  return <Navigate to={`/search`} replace state={{ codigo: codigo ? parseInt(codigo) : undefined }} />;
};

// Componente principal da aplicação
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (page: string) => {
    if (page === 'home') {
      navigate('/');
    } else {
      navigate(`/${page}`);
    }
  };

  const handleSearch = (searchData: any) => {
    navigate('/search', { state: { searchData } });
  };

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/locador')) return 'locador';
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/about')) return 'about';
    if (path.startsWith('/contato')) return 'contato';
    if (path.startsWith('/favoritos')) return 'favoritos';
    if (path.startsWith('/imovel/')) return 'property';
    return 'home';
  };

  return (
    <div className="App">
      <Header onNavigate={handleNavigate} currentPage={getCurrentPage()} />
      <main>
        <Routes>
          <Route path="/" element={<HomePage onSearch={handleSearch} onNavigate={handleNavigate} />} />
          <Route path="/search" element={<SearchPage onBackToHome={() => navigate('/')} />} />
          <Route path="/imovel/:codigo" element={<PropertyRedirect />} />
          <Route path="/locador" element={
            <ProtectedRoute requiredRoles={[UserRole.LOCADOR]}>
              <LocadorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contato" element={<ContactPage />} />
          <Route path="/favoritos" element={<FavoritosPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;